import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Layout,
  Input,
  DatePicker,
  Select,
  Modal,
  InputNumber,
  Descriptions,
  Tag,
  Tooltip,
} from "antd";
import {
  FileTextOutlined,
  FilterOutlined,
  SearchOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { getTicketParkingSearch, calculateTicketExtension, extendTicketParking } from "../api/ticketParking/ticketParkingRequest";
import { getAgentsForFilter } from "../api/constants";
import { getPaymentMethodInFrench, PAYMENT_METHODS } from "../utils/payment";
import { useSelector } from "react-redux";
import { SOURCE_TYPES } from "../utils/constants";
import PermissionRender from "../components/PermissionRender";
import dayjs from "dayjs";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;

function TicketParking() {
  const [ticketParking, setTicketParking] = useState([]);

  const [agents, setAgents] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [searchReference, setSearchReference] = useState("");
  const [searchMedthod, setSearchMethod] = useState("");
  const [selectedSource, setSelectedSource] = useState("");

  const [dateRange, setDateRange] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [loading, setLoading] = useState(false);

  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const user = useSelector((state) => state.user.user);

  // Extend ticket state
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [extendTicket, setExtendTicket] = useState(null);
  const [extendMinutes, setExtendMinutes] = useState(60);
  const [extendCalcResult, setExtendCalcResult] = useState(null);
  const [extendCalculating, setExtendCalculating] = useState(false);
  const [extendSubmitting, setExtendSubmitting] = useState(false);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    workerPdfRef.current = new Worker(
      new URL("../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message } = e.data;

      // Handle different status updates
      switch (status) {
        case "starting":
          console.log("PDF generation starting...");
          break;
        case "generating":
          console.log("Creating PDF document...");
          // You could update a more granular status message here if needed
          break;
        case "complete":
          console.log("PDF generation complete");

          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }

          break;
        case "error":
          console.error("PDF generation error:", message);
          // message.error(`Erreur lors de la génération du PDF: ${message}`);
          break;
        default:
          console.warn("Unknown status:", status);
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        console.log("error", error);
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchTicketParking = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        reference: searchReference,
        start_date: dateRange
          ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
          : null,
        end_date: dateRange ? dateRange[1].format("YYYY-MM-DD HH:mm:ss") : null,
        issued_by: selectedUser,
        payment_method: searchMedthod,
        source: selectedSource,
        page: pagination.current,
        per_page: pagination.pageSize,
      };
      const data = await getTicketParkingSearch(payload);
      setTicketParking(data); // Update state with the fetched data
      if (data.current_page > data.last_page)
        setPagination({ ...pagination, current: data.last_page });
    } catch (error) {
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  }, [
    searchReference,
    dateRange,
    selectedUser,
    searchMedthod,
    selectedSource,
    pagination,
  ]);

  useEffect(() => {
    fetchTicketParking();
  }, [fetchTicketParking]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAgentsForFilter();
      setAgents(data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const getStreetName = (record) => {
    if (record.street) {
      return record.street.name;
    }

    return "N/A";
  };

  const getZoneName = (record) => {
    if (record?.street?.section?.zone?.name) {
      return record.street.section.zone.name;
    }
    return "N/A";
  };

  const handleExcelClick = async () => {
    setIsExportingExcel(true);
    try {
      const timestamp = new Date().toISOString().replace(/:/g, "-");
      const filename = `Parking_ticket_${timestamp}.xlsx`;
      const baseUrl = window.location.origin;

      const filters = {
        reference: searchReference,
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        issued_by: selectedUser,
        payment_method: searchMedthod,
        source: selectedSource,
        no_pagination: true,
      };
      const dataSource = await getTicketParkingSearch(filters);

      // Prepare only the necessary data to reduce processing overhead
      const formattedData = dataSource.map((it) => ({
        reference: it.reference,
        start_date: it.start_date,
        end_date: it.end_date,
        payment_method: getPaymentMethodInFrench(it.payment_method),
        source: SOURCE_TYPES[it.source],
        amount: it.amount,
        vehicle_number: it.vehicle_number,
        street: getStreetName(it),
        zone: getZoneName(it),
        created_by: it.issued_by?.full_name,
      }));

      const data = [
        ["Parking Tickets", ``, ``, ``],
        [],
        [],
        user.full_name,
        [],
        [
          [
            {
              text: "Référence",
              type: "string",
              key: "reference",
            },
            {
              text: "Date de début",
              type: "string",
              key: "start_date",
            },
            {
              text: "Date de fin",
              type: "string",
              key: "end_date",
            },
            {
              text: "Méthode de paiement",
              type: "string",
              key: "payment_method",
            },
            {
              text: "Montant",
              type: "int",
              key: "amount",
            },
            {
              text: "Matricule",
              type: "string",
              key: "vehicle_number",
            },

            {
              text: "Contrat",
              type: "string",
              key: "zone",
            },
            {
              text: "Rue",
              type: "string",
              key: "street",
            },
            {
              text: "Créé par",
              type: "string",
              key: "created_by",
            },
            {
              text: "Source",
              type: "string",
              key: "source",
            },
          ],
        ],
        [formattedData],
        baseUrl,
      ];

      // Post message to worker
      workerExcelRef.current.postMessage({ data, filename });
    } catch (error) {
      console.error("Error generating Excel file:", error);
      message.error("Erreur lors de la génération du fichier Excel");
    } finally {
      setIsExportingExcel(false);
    }
  };

  const isTicketActive = (record) => {
    return record.end_date && dayjs(record.end_date).isAfter(dayjs());
  };

  const openExtendModal = (record) => {
    setExtendTicket(record);
    setExtendMinutes(60);
    setExtendCalcResult(null);
    setExtendModalVisible(true);
  };

  const handleCalculateExtension = async () => {
    if (!extendTicket || !extendMinutes) return;
    setExtendCalculating(true);
    try {
      const res = await calculateTicketExtension(extendTicket.id, extendMinutes);
      if (res.success) {
        setExtendCalcResult(res.data);
      } else {
        message.error(res.message || "Échec du calcul");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Échec du calcul de la prolongation");
    } finally {
      setExtendCalculating(false);
    }
  };

  const handleConfirmExtend = async () => {
    if (!extendTicket || !extendMinutes) return;
    setExtendSubmitting(true);
    try {
      const res = await extendTicketParking(extendTicket.id, extendMinutes);
      if (res.success) {
        message.success("Ticket prolongé avec succès");
        setExtendModalVisible(false);
        setExtendTicket(null);
        setExtendCalcResult(null);
        fetchTicketParking();
      } else {
        message.error(res.message || "Échec de la prolongation");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Échec de la prolongation du ticket");
    } finally {
      setExtendSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: "Date de début",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "Date de fin",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Méthode de paiement",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (text) => getPaymentMethodInFrench(text),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      render: (text) => (
        <div style={{ textAlign: "right" }}>{parseFloat(text).toFixed(2)}</div>
      ),
    },
    {
      title: "Matricule",
      dataIndex: "vehicle_number",
      key: "vehicle_number",
    },
    {
      title: "Contrat",
      key: "zone",
      render: (_, record) => getZoneName(record),
    },
    {
      title: "Rue",
      key: "street",
      render: (_, record) => getStreetName(record),
    },
    {
      title: "Créé par",
      dataIndex: "created_by",
      key: "created_by",
      render: (_, record) => record.issued_by?.full_name,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (_, record) => SOURCE_TYPES[record.source],
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => {
        const active = isTicketActive(record);
        return active ? (
          <PermissionRender requiredPermission={["ticket_parking", "extend"]}>
          <Tooltip title="Prolonger le ticket">
            <Button
              type="primary"
              size="small"
              icon={<ClockCircleOutlined />}
              onClick={() => openExtendModal(record)}
            >
              Prolonger
            </Button>
          </Tooltip>
          </PermissionRender> 
        ) : (
          <Tag color="default">Expiré</Tag>
        );
      },
    },
  ];

  return (
    <Layout className="min-h-screen ">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  className="w-full"
                  placeholder={["Date début", "Date fin"]}
                  showTime={{ format: "HH:mm" }}
                />
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Input
                  placeholder="Rechercher par Référence"
                  prefix={<SearchOutlined />}
                  value={searchReference}
                  onChange={(e) => setSearchReference(e.target.value)}
                />
              </Col>

              <Col xs={24} sm={12} md={5}>
                <Select
                  placeholder="Filtrer par Méthode de paiement"
                  style={{ width: "100%" }}
                  onChange={(value) => setSearchMethod(value)}
                  allowClear
                >
                  {/* Populate this with options as needed */}
                  {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Select
                  showSearch
                  placeholder="Filtrer par agent"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedUser(value)}
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {agents.map((type) => (
                    <Option key={type.id_agent} value={type.id_agent}>
                      {type.full_name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Row>
              <Col xs={24} sm={12} md={5}>
                <Select
                  placeholder="Filtrer par source"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedSource(value)}
                  allowClear
                >
                 {Object.entries(SOURCE_TYPES).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={fetchTicketParking}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        <Card className="shadow-sm">
          <div style={{ marginBottom: 16 }} className="flex justify-end">
            <Button
              icon={<FileTextOutlined />}
              onClick={async () => await handleExcelClick()}
              className="hover:border-green-700 hover:text-green-500 mr-4"
              disabled={isExportingExcel}
            >
              Exporter Excel
            </Button>
          </div>
          <Table
            dataSource={ticketParking.data}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              total: ticketParking.total,
              current: pagination.current,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${ticketParking.total} tickets`,
              onChange: (page, pageSize) => {
                setPagination({ ...pagination, current: page, pageSize });
              },
            }}
            loading={loading}
          />
        </Card>

        {/* Extend Ticket Modal */}
        <Modal
          title={
            <Space>
              <ClockCircleOutlined />
              <span>Prolonger le ticket {extendTicket?.reference}</span>
            </Space>
          }
          open={extendModalVisible}
          onCancel={() => {
            setExtendModalVisible(false);
            setExtendTicket(null);
            setExtendCalcResult(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setExtendModalVisible(false);
                setExtendTicket(null);
                setExtendCalcResult(null);
              }}
            >
              Annuler
            </Button>,
            !extendCalcResult ? (
              <Button
                key="calculate"
                type="primary"
                loading={extendCalculating}
                onClick={handleCalculateExtension}
                disabled={!extendMinutes || extendMinutes < 15}
              >
                Calculer le coût
              </Button>
            ) : (
              <Button
                key="confirm"
                type="primary"
                loading={extendSubmitting}
                onClick={handleConfirmExtend}
              >
                Confirmer la prolongation
              </Button>
            ),
          ]}
          width={520}
        >
          {extendTicket && (
            <Space direction="vertical" size="middle" className="w-full">
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Référence">{extendTicket.reference}</Descriptions.Item>
                <Descriptions.Item label="Matricule">{extendTicket.vehicle_number}</Descriptions.Item>
                <Descriptions.Item label="Fin actuelle">{extendTicket.end_date}</Descriptions.Item>
              </Descriptions>

              <div>
                <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                  Durée supplémentaire (minutes) :
                </label>
                <InputNumber
                  min={15}
                  max={1440}
                  step={15}
                  value={extendMinutes}
                  onChange={(v) => {
                    setExtendMinutes(v);
                    setExtendCalcResult(null);
                  }}
                  style={{ width: "100%" }}
                  addonAfter="min"
                />
              </div>

              {extendCalcResult && (
                <Card size="small" style={{ background: "#f6ffed", borderColor: "#b7eb8f" }}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nouvelle fin">
                      <Tag color="green">{extendCalcResult.new_end_date}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Coût de la prolongation">
                      <strong>{extendCalcResult.extension_amount} MAD</strong> (TVA: {extendCalcResult.extension_tva} MAD)
                    </Descriptions.Item>
                    <Descriptions.Item label="Montant total">
                      <strong>{extendCalcResult.new_total_amount} MAD</strong>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}
            </Space>
          )}
        </Modal>
      </Content>
    </Layout>
  );
}

export default TicketParking;
